import React, { useEffect } from 'react';
import Button from '@albertsons/uds/molecule/Button';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import handleJsonUpload from '../services/HandleJSONUpload';
import handleJsonValidation from '../services/HandleJSONValidation';
import Environment from '../utils/Environment';
import { useToast } from '../layout/Layout';
import ErrorModal from '../common/ErrorModal';
import './ExcelTable.css';
import { getMandatoryFieldKeys } from './tabConfig'; // Import from tabConfig

interface Column {
  key: string;
  name: string;
}

interface ExcelTableProps {
  columns: Column[];
  handleClosePopup?: () => void;
  handleReset: () => void;
  selectedDC: string;
  tabType: string;
  filesFillTable: File[];
  setFilesFillTable: (files: File[]) => void;
  labelDateKey?: string | null;
  isLoading: boolean;
  isUploading: boolean;
  isFileValidated: boolean;
  setIsFileValidated: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
  rows: any[];
  setRows: React.Dispatch<React.SetStateAction<any[]>>;
  alertSeverityFillTable: 'success' | 'error' | 'warning' | 'info';
  setAlertSeverityFillTable: (severity: 'success' | 'error' | 'warning' | 'info') => void;
  setAlertMessageFillTable: (message: string | null) => void;
  alertMessageFillTable: string | null;
  handleCloseSnackbarFillTable: (event: React.SyntheticEvent | Event, reason?: string) => void;
  tableRef: React.RefObject<HTMLTableElement>;
  handlePaste: (event: React.ClipboardEvent<HTMLTableElement>) => void;
  startResize: (index: number, event: React.MouseEvent<HTMLDivElement>) => void;
  handleCellChange: (event: React.ChangeEvent<HTMLInputElement>, rowIndex: number, colKey: string) => void; 
  handleClick: (rowIndex: number, colKey: string) => void;
  filteredRows: any[];
  toggleRowSelection: (rowIndex: number) => void;
  addRow: () => void;
  deleteSelected: () => void;
  handleCopySelectedRows: () => void;
  bulkClearCells: (cellUpdates: { rowIndex: number; colKey: string }[]) => void;
  handleUndo: () => boolean;
  handleRedo: () => boolean;
  undoStack: any[];
  redoStack: any[];
  selectedRows: Set<number>;
  setSelectedRows: React.Dispatch<React.SetStateAction<Set<number>>>;
  editingCell: { row: number; col: string } | null;
  highlightMap: Record<string, boolean>;
  setHighlightMap: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  setEditingCell: React.Dispatch<React.SetStateAction<{ row: number; col: string } | null>>;
  handlePageSizeChange: (newPageSize: number) => void;
  handleJumpToPage: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  paginationModel: { page: number; pageSize: number };
  setPaginationModel: React.Dispatch<React.SetStateAction<{ page: number; pageSize: number }>>;
  jumpToPageError: string | null;
  setJumpToPageError: React.Dispatch<React.SetStateAction<string | null>>;
  jumpToPageValue: string;
  setJumpToPageValue: React.Dispatch<React.SetStateAction<string>>;
  currentPage: number;
  createPageRange: () => (number | string)[];
  validationErrors: Record<string, boolean>;
  setValidationErrors: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  userId?: string;
}

type Row = Record<string, string>;

export default function ExcelTable(
  // ...existing code...
  // ...existing code...
  // Destructure props
  { columns,
    handleClosePopup,
    handleReset,
    selectedDC,
    tabType,
    filesFillTable,
    setFilesFillTable,
    labelDateKey,
    isLoading,
    isUploading,
    setIsUploading,
    isFileValidated,
    setIsFileValidated,
    setIsLoading,
    rows,
    setRows,
    setAlertMessageFillTable,
    alertSeverityFillTable,
    alertMessageFillTable,
    setAlertSeverityFillTable,
    handleCloseSnackbarFillTable,
    tableRef,
    handlePaste,
    startResize,
    handleCellChange,
    handleClick,
    filteredRows,
    toggleRowSelection,
    selectedRows,
    setSelectedRows,
    addRow,
    deleteSelected,
    handleCopySelectedRows,
    bulkClearCells,
    handleUndo,
    handleRedo,
    undoStack,
    redoStack,
    editingCell,
    highlightMap,
    setHighlightMap,
    setEditingCell,
    handlePageSizeChange,
    handleJumpToPage,
    paginationModel,
    setPaginationModel,
    jumpToPageError,
    setJumpToPageError,
    jumpToPageValue,
    setJumpToPageValue,
    currentPage,
    createPageRange,
    validationErrors,
    setValidationErrors,
    userId
  }: ExcelTableProps) {
  
  // Get layout-level toast functions
  const { showSuccess: layoutShowSuccess, showError: layoutShowError, clearToast } = useToast();
  
  // State for error modal
  const [errorModalOpen, setErrorModalOpen] = React.useState(false);
  const [allErrors, setAllErrors] = React.useState<string[]>([]);
  const [errorModalTitle, setErrorModalTitle] = React.useState('All Errors');
  
  // Mock API state
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const [uploadResponse, setUploadResponse] = React.useState<any>(null);
  
  // Store the exact data from the initial upload attempt for consistency
  const [uploadNormalizedRows, setUploadNormalizedRows] = React.useState<any[]>([]);
  const [uploadRowCount, setUploadRowCount] = React.useState<number>(0);
  
  // Flag to prevent useEffect from overwriting validation highlights
  const [isValidationInProgress, setIsValidationInProgress] = React.useState(false);
  
  // Track the rows at the time of validation to detect actual data changes
  const rowsAtValidationRef = React.useRef<any[]>([]);

  const handleDeleteAndReindexErrors = () => {
    if (selectedRows.size === 0) return;

    // Adjust server-side validation messages from the alert
    if (alertMessageFillTable) {
        const newErrorMessages: string[] = [];
        const keptRowIndexMap = new Map<number, number>();
        let newIndex = 0;
        for (let i = 0; i < rows.length; i++) {
            if (!selectedRows.has(i)) {
                keptRowIndexMap.set(i, newIndex++);
            }
        }

        const errorLines = alertMessageFillTable.split(/Error in line/i).slice(1);
        errorLines.forEach(line => {
            const match = line.match(/\s*(?:number\s+)?(\d+)\s*[:-]\s*(.*)/);
            if (match) {
                const oldLineNum = parseInt(match[1].trim());
                const oldRowIndex = oldLineNum - 1;
                
                if (keptRowIndexMap.has(oldRowIndex)) {
                    const newRowIndex = keptRowIndexMap.get(oldRowIndex)!;
                    const message = match[2].trim();
                    newErrorMessages.push(`Error in line ${newRowIndex + 1}: ${message}`);
                }
            }
        });

        setAlertMessageFillTable(newErrorMessages.length > 0 ? newErrorMessages.join(', ') : null);
        if (newErrorMessages.length === 0) {
            setAlertSeverityFillTable('success');
        }
    }

    // Call the original delete function from the hook to update rows and client-side errors
    deleteSelected();
  };


  // Create wrapper functions to use layout-level toast only
  const showSuccess = (message: string) => {
    setAlertSeverityFillTable('success');
    setAlertMessageFillTable(null);
    layoutShowSuccess(message);
  };
  
  const showError = (message: string, fullErrors?: string[]) => {
    // Always store errors locally for modal, regardless of count
    if (fullErrors && fullErrors.length > 0) {
      setAllErrors(fullErrors);
      setErrorModalTitle(`Validation Errors`);
    }
    setAlertSeverityFillTable('error');
    setAlertMessageFillTable(message);
    // Always use the layout toast which will show "View All Errors" button when appropriate
    layoutShowError(message, fullErrors);
  };

  const handleCloseErrorModal = () => {
    setErrorModalOpen(false);
  };

  const handleCloseWithToastCleanup = () => {
    // Clear any active toast messages and "View All Errors" state
    clearToast();
    // Clear local error modal state as well
    setErrorModalOpen(false);
    setAllErrors([]);
    // Clear cell highlights
    setHighlightMap({});
    // Call the original close popup function
    handleClosePopup?.();
  };
  
  // Utility to normalize Shipment Dispatch Date in a row
  function normalizeShipmentDispatchDate(row: any) {
    if (row['Shipment Dispatch Date']) {
      row['Shipment Dispatch Date'] = row['Shipment Dispatch Date'].replace(/[-\\]+/g, '/');
    }
    return row;
  }

  // Highlight error cells for IN02 rows
  // Highlight error cells for IN02 and XDOCK rows
  const highlightErrors = React.useCallback((rowsToCheck: any[], tabType: string) => {
    const mandatoryFields = getMandatoryFieldKeys(tabType, selectedDC);
    let loadGroupFields: string[] = [];
    if (tabType === 'IN02') {
      // Check if this is a Custom IN02 DC
      const customIN02DCs = Environment.getCustomIN02DCList();
      const isCustomIN02DC = customIN02DCs.includes(selectedDC);
      
      // Add 'Stop #' as mandatory field for Custom IN02 DCs only
      if (isCustomIN02DC) {
        mandatoryFields.push('Stop #');
      }
      
      loadGroupFields = ['Load Group FRZ', 'Load Group GRO', 'Load Group PER', 'Load Group GML', 'Load Group PRO'];
    } 
    const map: Record<string, boolean> = {};
    rowsToCheck.forEach((row, rowIdx) => {
      const hasData = Object.entries(row).some(([key, value]) => key !== 'tempKey' && value !== '' && value !== null);
      if (hasData) {
        // Check if this is a Combined IN02 DC (e.g. WAUB)
        const combinedIN02DCs = Environment.getCombinedIN02DCList();
        const isCombinedIN02DC = tabType === 'IN02' && combinedIN02DCs.includes(selectedDC);

        if (isCombinedIN02DC) {
          // Combined IN02 (WAUB): only Facility and Shipment ID are mandatory
          ['Facility', 'Shipment ID'].forEach(field => {
            if (!row[field] || row[field].toString().trim() === '') {
              map[`${rowIdx}-${field}`] = true;
            }
          });
          // Highlight all Load Group fields if none are filled
          const hasLoadGroup = loadGroupFields.some(field => {
            const value = row[field];
            return value && value !== null && value.toString().trim() !== '';
          });
          if (!hasLoadGroup) {
            loadGroupFields.forEach(field => {
              map[`${rowIdx}-${field}`] = true;
            });
          }
        } else {
          // Check if this is a Custom IN02 scenario for Primary Staging Location exemption
          const customIN02DCs = Environment.getCustomIN02DCList();
          const isCustomIN02DC = tabType === 'IN02' && customIN02DCs.includes(selectedDC);
          const facilityValue = row['Facility'];
          const stopDestinationFacility = row['Stop Destination Facility'];
          let isPrimaryStagingLocationExempt = false;
          
          // For Custom IN02 DCs: if Facility equals Stop Destination Facility, Primary Staging Location and Planned Door are not required
          if (isCustomIN02DC && facilityValue && stopDestinationFacility) {
            const facility = facilityValue.toString().trim();
            const stopDest = stopDestinationFacility.toString().trim();
            isPrimaryStagingLocationExempt = facility === stopDest;
          }
          
          mandatoryFields.forEach(field => {
            // Skip Primary Staging Location and Planned Door highlighting if Custom IN02 exemption applies
            if ((field === 'Primary Staging Location' || field === 'Planned Door') && isPrimaryStagingLocationExempt) {
              return; // Skip highlighting this field
            }
            
            if (!row[field] || row[field].toString().trim() === '') {
              map[`${rowIdx}-${field}`] = true;
            }
          });
          if (tabType === 'IN02') {
            const hasLoadGroup = loadGroupFields.some(field => {
              const value = row[field];
              return value && value !== null && value.toString().trim() !== '';
            });
            if (!hasLoadGroup) {
              loadGroupFields.forEach(field => {
                map[`${rowIdx}-${field}`] = true;
              });
            }
          }
        }
        // Facility validation for both tabs
        if (row['Facility'] && row['Facility'].toString().trim() !== '') {
          const facilityValue = row['Facility'].toString().trim();
          
          // Check length constraints (min 4, max 5 characters)
          const isValidLength = facilityValue.length >= 4 && facilityValue.length <= 5;
          
          // Check if facility matches selected DC pattern
          const allowedPatterns = [
            selectedDC, // Exact DC match
            `${selectedDC}C`, // DC + C
            `${selectedDC}D`, // DC + D
            `${selectedDC}Q`, // DC + Q
            `${selectedDC}U`  // DC + U
          ];
          const isValidPattern = allowedPatterns.includes(facilityValue);
          
          if (!isValidLength || !isValidPattern) {
            map[`${rowIdx}-Facility`] = true;
          }
        }
        
        // Date validation for IN02
        if (tabType === 'IN02' && row['Shipment Dispatch Date'] && row['Shipment Dispatch Date'].toString().trim() !== '') {
          const { isValid } = validateAndFormatDate(row['Shipment Dispatch Date'].toString());
          if (!isValid) {
            map[`${rowIdx}-Shipment Dispatch Date`] = true;
          }
        }
        
        // Stop Destination Facility validation for Custom IN02
        if (tabType === 'IN02' && row['Stop Destination Facility'] && row['Stop Destination Facility'].toString().trim() !== '') {
          const customIN02DCs = Environment.getCustomIN02DCList();
          const isCustomIN02DC = customIN02DCs.includes(selectedDC);
          
          if (isCustomIN02DC) {
            const stopDestValue = row['Stop Destination Facility'].toString().trim();
            if (stopDestValue.length >= 4) {
              const first4Chars = stopDestValue.substring(0, 4);
              const isCustomIN02StopDest = customIN02DCs.includes(first4Chars);
              
              if (isCustomIN02StopDest) {
                let hasStopDestError = false;
                
                // Check length constraints
                if (stopDestValue.length < 4 || stopDestValue.length > 5) {
                  hasStopDestError = true;
                }
                // If 5 characters, check 5th character
                else if (stopDestValue.length === 5) {
                  const fifthChar = stopDestValue.charAt(4).toUpperCase();
                  if (!['C', 'D', 'Q', 'U'].includes(fifthChar)) {
                    hasStopDestError = true;
                  }
                }
                
                if (hasStopDestError) {
                  map[`${rowIdx}-Stop Destination Facility`] = true;
                }
              }
            }
          }
        }
        
        // Stop # validation for Custom IN02
        if (tabType === 'IN02' && row['Stop #'] && row['Stop #'].toString().trim() !== '') {
          const customIN02DCs = Environment.getCustomIN02DCList();
          const isCustomIN02DC = customIN02DCs.includes(selectedDC);
          
          if (isCustomIN02DC) {
            const stopValue = row['Stop #'].toString().trim();
            // Check if Stop # contains an underscore
            if (!stopValue.includes('_')) {
              map[`${rowIdx}-Stop #`] = true;
            }
          }
        }
      }
    });
    // Return the map instead of setting state directly
    return map;
  }, [selectedDC]);

  const displayRows = rows.length < 25 ? filteredRows : rows;

  // Date format validation and conversion function
  const validateAndFormatDate = (dateString: string): { isValid: boolean; formattedDate: string } => {
    if (!dateString || dateString.trim() === '') return { isValid: true, formattedDate: '' };
    
    const dateStr = dateString.trim();
    
    try {
      let date: Date | null = null;
      
      // Pattern 1: m/d/yyyy h:mm, m/d/yyyy hh:mm, m/dd/yyyy h:mm, m/dd/yyyy hh:mm, mm/d/yyyy h:mm, mm/d/yyyy hh:mm, mm/dd/yyyy h:mm, mm/dd/yyyy hh:mm
      const mdyPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})$/;
      const mdyMatch = dateStr.match(mdyPattern);
      if (mdyMatch) {
        const [, month, day, year, hour, minute] = mdyMatch;
        // Create date with month/day/year format
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute), 0);
        
        // Validate the created date matches input (to catch invalid dates)
        if (date.getMonth() === parseInt(month) - 1 && 
            date.getDate() === parseInt(day) && 
            date.getFullYear() === parseInt(year)) {
          // Valid date, continue to formatting
        } else {
          date = null; // Invalid date, will try other patterns
        }
      }
      
      // Pattern 2: m/d/yyyy hh:mm:ss AM/PM (12-hour format with seconds and AM/PM)
      if (!date) {
        const ampmPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})\s+(AM|PM|am|pm)$/;
        const ampmMatch = dateStr.match(ampmPattern);
        if (ampmMatch) {
          const [, month, day, year, hour, minute, second, ampm] = ampmMatch;
          let hour24 = parseInt(hour);
          
          // Convert 12-hour to 24-hour format
          if (ampm.toUpperCase() === 'PM' && hour24 !== 12) {
            hour24 += 12;
          } else if (ampm.toUpperCase() === 'AM' && hour24 === 12) {
            hour24 = 0;
          }
          
          date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), hour24, parseInt(minute), parseInt(second));
          
          // Validate the created date matches input (to catch invalid dates)
          if (date.getMonth() === parseInt(month) - 1 && 
              date.getDate() === parseInt(day) && 
              date.getFullYear() === parseInt(year)) {
            // Valid date, continue to formatting
          } else {
            date = null; // Invalid date, will try other patterns
          }
        }
      }
      
      // Pattern 3: yyyy-mm-dd hh:mm:ss (ISO format)
      if (!date) {
        const isoPattern = /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/;
        const isoMatch = dateStr.match(isoPattern);
        if (isoMatch) {
          const [, year, month, day, hour, minute, second] = isoMatch;
          date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute), parseInt(second));
        }
      }
      
      if (date && !isNaN(date.getTime())) {
        // Validate date is reasonable
        if (date.getFullYear() < 1900 || date.getFullYear() > 2100) {
          return { isValid: false, formattedDate: dateStr };
        }
        
        // Format to mm/dd/yyyy hh:mm (US format without seconds)
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hour = date.getHours().toString().padStart(2, '0');
        const minute = date.getMinutes().toString().padStart(2, '0');
        
        const formatted = `${month}/${day}/${year} ${hour}:${minute}`;
        return { isValid: true, formattedDate: formatted };
      }
      
      // Check if already in correct format mm/dd/yyyy hh:mm
      const correctFormatPattern = /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})$/;
      const correctMatch = dateStr.match(correctFormatPattern);
      if (correctMatch) {
        const [, month, day, year, hour, minute] = correctMatch;
        const testDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), parseInt(hour), parseInt(minute), 0);
        
        if (!isNaN(testDate.getTime()) && 
            testDate.getDate() === parseInt(day) && 
            testDate.getMonth() === parseInt(month) - 1 &&
            testDate.getFullYear() === parseInt(year)) {
          return { isValid: true, formattedDate: dateStr };
        }
      }
      
      return { isValid: false, formattedDate: dateStr };
    } catch {
      return { isValid: false, formattedDate: dateStr };
    }
  };



  // Handle user confirmation to proceed despite wave running
  const handleProceed = () => {
    setShowConfirmDialog(false);
    // User clicked Proceed, so re-call the upload function with isProceed = true
    // Use the exact same data from the initial upload attempt
    handleJsonUpload(
      selectedDC,
      tabType,
      handleReset,
      uploadNormalizedRows,
      uploadRowCount,
      setIsUploading,
      showSuccess,
      showError,
      undefined, // No onWaveRunning callback needed this time
      true, // Set isProceed to true
      userId,
      new Date().toISOString()
    );
    setUploadResponse(null);
  };

  // Handle user cancellation
  const handleCancel = () => {
    setShowConfirmDialog(false);
    setUploadResponse(null);
  };

  // Check for empty rows in between data (not after the last data row)
  const checkForEmptyRows = (): boolean => {
    // Find the index of the last row with data
    let lastDataRowIndex = -1;
    for (let i = rows.length - 1; i >= 0; i--) {
      const hasData = Object.entries(rows[i]).some(([key, value]) => 
        key !== 'tempKey' && value !== '' && value !== null && value !== undefined
      );
      if (hasData) {
        lastDataRowIndex = i;
        break;
      }
    }
    
    // If no data rows found, no need to check for empty rows in between
    if (lastDataRowIndex === -1) {
      return false;
    }
    
    // Check for empty rows only up to the last data row
    const emptyRowsInBetween = [];
    for (let i = 0; i <= lastDataRowIndex; i++) {
      const hasData = Object.entries(rows[i]).some(([key, value]) => 
        key !== 'tempKey' && value !== '' && value !== null && value !== undefined
      );
      if (!hasData) {
        emptyRowsInBetween.push(i);
      }
    }
    
    if (emptyRowsInBetween.length > 0) {
      const rowNumbers = emptyRowsInBetween.map(idx => idx + 1).join(', ');
      const errorMessage = `Empty ${emptyRowsInBetween.length === 1 ? 'row' : 'rows'} detected: ${rowNumbers}. Please remove all empty rows before validating.`;
      showError(errorMessage);
      return true; // Has empty rows in between data
    }
    return false; // No empty rows in between data
  };

  const validateMandatoryFields = (): boolean => {
    // Define tab-specific mandatory fields and validation rules
    const tabConfig: Record<string, { mandatoryFields: string[], extraValidation?: (row: any, lineErrors: string[]) => void }> = {
      IN02: {
       mandatoryFields: getMandatoryFieldKeys('IN02', selectedDC),
        extraValidation: (row, lineErrors) => {
          const loadGroupFields = ['Load Group FRZ', 'Load Group GRO', 'Load Group PER', 'Load Group GML', 'Load Group PRO'];
          const loadGroupFieldNames = ['FRZ', 'GRO', 'PER', 'GML', 'PRO'];
          
          // Check if this is a Custom IN02 DC
          const customIN02DCs = Environment.getCustomIN02DCList();
          const isCustomIN02DC = customIN02DCs.includes(selectedDC);

          // Check if this is a Combined IN02 DC (e.g. WAUB)
          const combinedIN02DCs = Environment.getCombinedIN02DCList();
          const isCombinedIN02DC = combinedIN02DCs.includes(selectedDC);
          
          // Helper function to validate load group value based on DC type
          const isValidLoadGroupValue = (value: any): boolean => {
            if (!value && value !== 0) return true; // Empty values are allowed
            
            const stringValue = String(value).trim();
            if (stringValue === '') return true; // Empty string is allowed
            
            if (isCustomIN02DC) {
              // Custom IN02: Allow any numerical value > 0
              if (!/^[0-9]+$/.test(stringValue)) return false; // Must contain only digits
              const numValue = parseInt(stringValue, 10);
              return numValue > 0;
            } else if (isCombinedIN02DC) {
              // Combined IN02 (e.g. WAUB): Allow any numerical value > 0
              if (!/^[0-9]+$/.test(stringValue)) return false; // Must contain only digits
              const numValue = parseInt(stringValue, 10);
              return numValue > 0;
            } else {
              // Normal IN02: Must be 1 or 2 digits (1-99)
              if (!/^\d{1,2}$/.test(stringValue)) return false;
              
              // Parse and validate range: must be between 1-99
              const numValue = parseInt(stringValue, 10);
              return numValue > 0 && numValue <= 99;
            }
          };
          
          let hasAtLeastOneLoadGroup = false;
          
          // Check each Load Group field for both presence and format
          loadGroupFields.forEach((field, index) => {
            const value = row[field];
            const fieldName = loadGroupFieldNames[index];
            
            // Check if field has a value
            const hasValue = value && value !== null && 
                           (typeof value === 'string' ? value.trim() !== '' : 
                            typeof value === 'number' ? true : 
                            String(value).trim() !== '');
            
            if (hasValue) {
              hasAtLeastOneLoadGroup = true;
              
              // Validate the format based on DC type
              if (!isValidLoadGroupValue(value)) {
                const errorMsg = (isCustomIN02DC || isCombinedIN02DC)
                  ? `Load Group ${fieldName} must contain only numerical values greater than 0. Invalid value: "${value}"`
                  : `Load Group ${fieldName} must be a number from 1 to 99. Invalid value: "${value}"`;
                lineErrors.push(errorMsg);
              }
            }
          });
          
          if (!hasAtLeastOneLoadGroup) {
            lineErrors.push('At least one Load Group field (FRZ, GRO, PER, GML, PRO) must have a value');
          }
          
          // Date validation for Shipment Dispatch Date
          const dispatchDateValue = row['Shipment Dispatch Date'];
          if (dispatchDateValue && dispatchDateValue.toString().trim() !== '') {
            const { isValid, formattedDate } = validateAndFormatDate(dispatchDateValue.toString());
            if (!isValid) {
              lineErrors.push('Shipment Dispatch Date must be in mm/dd/yyyy hh:mm format');
            } else {
              // Update the row data with formatted date
              row['Shipment Dispatch Date'] = formattedDate;
            }
          }
        }
      },
      XDOCK: {
        mandatoryFields: [
          'SourceLocationCode', 'DestinationLocationCode', 'Cubes', 'ReferenceID' , 'CommodityCode', 'Weight', 'Cases', 'Comments'
        ],
        extraValidation: (row, lineErrors) => {
          // SourceLocationCode
          if (!row['SourceLocationCode'] || row['SourceLocationCode'].toString().trim() === '') {
            lineErrors.push('SourceLocationCode must not be null');
          }
          // DestinationLocationCode
          if (!row['DestinationLocationCode'] || row['DestinationLocationCode'].toString().trim() === '') {
            lineErrors.push('DestinationLocationCode must not be null');
          }
          // ReferenceID
          const referenceIDValue = row['ReferenceID'];
          if (!referenceIDValue || referenceIDValue.toString().trim() === '') {
            lineErrors.push('ReferenceID must not be null');
          } else {
            const specialCharPattern = /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9\s-]+$/;
            if (!specialCharPattern.test(referenceIDValue.toString().trim())) {
              lineErrors.push('ReferenceID must not contain special characters');
            }
          }
          // Cubes must be a valid decimal value >= 1
          const cubesValue = row['Cubes'];
          if (cubesValue === undefined || cubesValue === null || cubesValue.toString().trim() === '') {
            lineErrors.push('Cubes must be a valid decimal value');
          } else {
            const cubesNum = Number(cubesValue);
            if (isNaN(cubesNum) || cubesNum < 0.1) {
              lineErrors.push('Cubes must be a valid decimal value');
            }
          }
          // CommodityCode
          const commodityCodeValue = row['CommodityCode'];
          if (!commodityCodeValue || commodityCodeValue.toString().trim() === '') {
            lineErrors.push('CommodityCode must not be null');
          }else {
             const specialCharPattern = /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9\s-]+$/;
            if (!specialCharPattern.test(commodityCodeValue.toString().trim())) {
              lineErrors.push('CommodityCode must not contain special characters');
            }
          }
          // Weight
          const weightValue = row['Weight'];
          if (weightValue === undefined || weightValue === null || weightValue.toString().trim() === '') {
            lineErrors.push('Weight must be a valid decimal value');
          } else {
            const weightNum = Number(weightValue);
            if (isNaN(weightNum) || weightNum< 0.1) {
              lineErrors.push('Weight must be a valid decimal value');
            }
          }
          // Cases
          const casesValue = row['Cases'];
          if (casesValue === undefined || casesValue === null || casesValue.toString().trim() === '') {
            lineErrors.push('Cases must be a valid decimal value');
          } else {
            const casesNum = Number(casesValue);
            if (isNaN(casesNum) || casesNum< 0.1) {
              lineErrors.push('Cases must be a valid decimal value');
            }
          }
          // Pallets
          const palletsValue = row['Pallets'];
          if (!(palletsValue === undefined || palletsValue === null || palletsValue.toString().trim() === '')) {
            const palletsNum = Number(palletsValue);
            if (isNaN(palletsNum) || palletsNum < 0.1) {
              lineErrors.push('Pallets must be a valid decimal value');
            }
          }
          // Comments
          const commentsValue = row['Comments'];
          if (!commentsValue || commentsValue.toString().trim() === '') {
            lineErrors.push('Comments must not be null');
          }else {
            const specialCharPattern = /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9\s/&-]+$/;
            if (!specialCharPattern.test(commentsValue.toString().trim())) {
              lineErrors.push('Comments must not contain special characters');
            }
          }
        }
      }
      // Add more tab configs here as needed
    };

    const errorsByLine: Record<number, string[]> = {};

    rows.forEach((row, index) => {
      // Check if row has any data (skip completely empty rows)
      const hasData = Object.entries(row).some(([key, value]) => 
        key !== 'tempKey' && value !== '' && value !== null
      );

      if (hasData) {
        const lineNumber = index + 1;
        const lineErrors: string[] = [];

        // Validate Facility column (first since it's the first column)
        if (row['Facility'] && row['Facility'].toString().trim() !== '') {
          const facilityValue = row['Facility'].toString().trim();
          
          // Check length constraints (min 4, max 5 characters)
          if (facilityValue.length < 4 || facilityValue.length > 5) {
            lineErrors.push(`Facility must be between 4 and 5 characters`);
          }
          
          // Check if facility matches selected DC pattern
          const allowedPatterns = [
            selectedDC, // Exact DC match
            `${selectedDC}C`, // DC + C
            `${selectedDC}D`, // DC + D
            `${selectedDC}Q`, // DC + Q
            `${selectedDC}U`  // DC + U
          ];
          
          if (!allowedPatterns.includes(facilityValue)) {
            lineErrors.push(`Facility must be ${selectedDC} or ${selectedDC} followed by C, D, Q, or U`);
          }
        }

        // Get config for current tabType, fallback to IN02 if not found
        const config = tabConfig[tabType] || tabConfig['IN02'];

        // For IN02, use generic mandatory field error messages
        if (tabType === 'IN02') {
          // Check if this is a Combined IN02 DC (e.g. WAUB)
          const combinedIN02DCs = Environment.getCombinedIN02DCList();
          const isCombinedIN02DC = combinedIN02DCs.includes(selectedDC);

          if (isCombinedIN02DC) {
            // Combined IN02 (WAUB): only Facility and Shipment ID are mandatory
            ['Facility', 'Shipment ID'].forEach(field => {
              if (!row[field] || row[field].toString().trim() === '') {
                lineErrors.push(`${field} is mandatory and cannot be empty`);
              }
            });
          } else {
            // Check if this is a Custom IN02 scenario for Primary Staging Location exemption
            const customIN02DCs = Environment.getCustomIN02DCList();
            const isCustomIN02DC = customIN02DCs.includes(selectedDC);
            const facilityValue = row['Facility'];
            const stopDestinationFacility = row['Stop Destination Facility'];
            let isPrimaryStagingLocationExempt = false;
            
            // For Custom IN02 DCs: if Facility equals Stop Destination Facility, Primary Staging Location and Planned Door are not required
            if (isCustomIN02DC && facilityValue && stopDestinationFacility) {
              const facility = facilityValue.toString().trim();
              const stopDest = stopDestinationFacility.toString().trim();
              isPrimaryStagingLocationExempt = facility === stopDest;
            }
            
            config.mandatoryFields.forEach(field => {
              // Skip Primary Staging Location and Planned Door validation if Custom IN02 exemption applies
              if ((field === 'Primary Staging Location' || field === 'Planned Door') && isPrimaryStagingLocationExempt) {
                return; // Skip this mandatory field validation
              }
              
              if (!row[field] || row[field].toString().trim() === '') {
                lineErrors.push(`${field} is mandatory and cannot be empty`);
              }
            });
            
            // Additional validation for Stop Destination Facility when it belongs to Custom IN02 DC
            if (isCustomIN02DC && stopDestinationFacility && stopDestinationFacility.toString().trim() !== '') {
              const stopDestValue = stopDestinationFacility.toString().trim();
              if (stopDestValue.length >= 4) {
                const first4Chars = stopDestValue.substring(0, 4);
                const isCustomIN02StopDest = customIN02DCs.includes(first4Chars);
                
                if (isCustomIN02StopDest) {
                  // Validate length constraints (min 4, max 5 characters)
                  if (stopDestValue.length < 4 || stopDestValue.length > 5) {
                    lineErrors.push(`Stop Destination Facility must be between 4 and 5 characters for Custom IN02`);
                  }
                  // If 5 characters, validate that 5th character is C, D, Q, or U
                  else if (stopDestValue.length === 5) {
                    const fifthChar = stopDestValue.charAt(4).toUpperCase();
                    if (!['C', 'D', 'Q', 'U'].includes(fifthChar)) {
                      lineErrors.push(`Stop Destination Facility 5th character must be C, D, Q, or U for Custom IN02`);
                    }
                  }
                }
              }
            }
            
            // Additional validation for Stop # in Custom IN02 DC
            if (isCustomIN02DC) {
              const stopValue = row['Stop #'];
              if (stopValue && stopValue.toString().trim() !== '') {
                const stopValueStr = stopValue.toString().trim();
                // Check if Stop # contains an underscore
                if (!stopValueStr.includes('_')) {
                  lineErrors.push(`Stop # must contain an underscore (_)`);
                }
              }
            }
          }
        }
        // For XDOCK, only use the specific error messages from extraValidation
        if (config.extraValidation) {
          config.extraValidation(row, lineErrors);
        }

        // If there are errors for this line, add them to the errorsByLine object
        if (lineErrors.length > 0) {
          errorsByLine[lineNumber] = lineErrors;
        }
      }
    });
    
    // Format errors as a single line, separated by commas
    if (Object.keys(errorsByLine).length > 0) {
      const formattedErrors = Object.entries(errorsByLine).map(([lineNum, lineErrors]) => {
        return `Error in line ${lineNum} - ${lineErrors.join(', ')}`;
      });
      const errorMessage = formattedErrors.join(', ');
      
      // Show "View All Errors" when there are more than 1 error
      const shouldShowViewAll = formattedErrors.length > 1;
      
      // Pass the individual errors if there are more than 1 error for the "View All Errors" functionality
      showError(errorMessage, shouldShowViewAll ? formattedErrors : undefined);
      return false;
    }
    // Don't clear highlights here - they will be cleared by the validation success handler
    // or overwritten by the validation failure handler
    return true;
  };

  // COMBINED useEffect for all highlighting logic
  useEffect(() => {
    // If a validation call is in progress, don't change any highlights.
    // The highlights from the validation result will be handled by this same effect once the call completes.
    if (isValidationInProgress) {
      return;
    }

    // Start with an empty map
    const newHighlightMap: Record<string, boolean> = {};

    // 1. Add highlights from server-side validation (alertMessageFillTable)
    if (alertSeverityFillTable === 'error' && alertMessageFillTable) {
      const errorLines = alertMessageFillTable.split(/Error in line/i).slice(1);
      errorLines.forEach(line => {
        const match = line.match(/\s*(?:number\s+)?(\d+)\s*[:-]\s*(.*)/);
        if (match) {
          const lineNum = (parseInt(match[1].trim()) - 1).toString();
          const message = match[2].trim();
      

          columns.forEach(({ key, name }) => {
            const normalizedLabel = name.replace(/\s+/g, '').toLowerCase();
            const normalizedKey = key.toLowerCase();
            const normalizedMessage = message.replace(/\s+/g, '').toLowerCase();
            
            // Split on commas to handle multiple errors in one line
            const subErrors = normalizedMessage.split(',');
            
          //  const isLoadGroupField = key.startsWith('Load Group');
            
            let shouldHighlight = false;
            for (const subError of subErrors) {
                // Only match the field that has the error, not fields mentioned after "when"
                const errorPart = subError.split('when')[0];
                
                const isFacilityError = errorPart.includes('facility') && key === 'Facility';
                const hasKeyMatch = errorPart.includes(normalizedKey);
                const hasLabelMatch = errorPart.includes(normalizedLabel);
                let isFieldError = hasKeyMatch || hasLabelMatch;
                
                // Avoid false positives from substring matches (e.g., "planneddoor" matching inside "secondaryplanneddoor")
                // If this column's name is found as a substring of a longer column name that is also in the error, skip it
                if (isFieldError && !hasKeyMatch) {
                    const isPartOfLongerColumnName = columns.some(otherCol => {
                        if (otherCol.key === key) return false;
                        const otherLabel = otherCol.name.replace(/\s+/g, '').toLowerCase();
                        return otherLabel.length > normalizedLabel.length &&
                               otherLabel.includes(normalizedLabel) &&
                               errorPart.includes(otherLabel);
                    });
                    if (isPartOfLongerColumnName) {
                        isFieldError = false;
                    }
                }
                
                if (isFacilityError || isFieldError) {
                    shouldHighlight = true;
                    break;
                }
            }
            
            if (shouldHighlight) {
                newHighlightMap[`${lineNum}-${key}`] = true;
            }
        });

          if (tabType === 'IN02') {
            const specialCases = [
              { fields: ['Planned Door', 'Secondary Planned Door', 'Tertiary Planned Door'], messagePattern: "Door", regex: /Doors? '([^']+)' (?:are|is) not present in MA/ },
              { fields: ['Primary Staging Location', 'Secondary Staging Location', 'Tertiary Staging Location'], messagePattern: "Staging Location", regex: /Staging Locations? '([^']+)' (?:are|is) not present in MA/i },
            ];
            specialCases.forEach(({ fields, messagePattern, regex }) => {
              if (message.includes(messagePattern)) {
                const match = message.match(regex);
                if (match) {
                  const invalidValues = match[1].split(',').map(val => val.replace(/^['"]|['"]$/g, '').trim());
                  fields.forEach(field => {
                    rows.forEach((row, rowIdx) => {
                      const cellValue = row[field]?.toString().trim().replace(/^['"]|['"]$/g, '');
                      if (cellValue) {
                        // For staging locations, check both exact match and zero-padded match (length 3)
                        if (field.includes('Staging Location')) {
                          const paddedCellValue = cellValue.padStart(3, '0');
                          const isMatch = invalidValues.some(invalidValue => {
                            const paddedInvalidValue = invalidValue.padStart(3, '0');
                            return cellValue.toLowerCase() === invalidValue.toLowerCase() || 
                                   paddedCellValue.toLowerCase() === paddedInvalidValue.toLowerCase();
                          });
                          if (isMatch) {
                            newHighlightMap[`${rowIdx}-${field}`] = true;
                          }
                        } else {
                          // For other fields (like Doors), use exact match only
                          if (invalidValues.some(invalidValue => cellValue.toLowerCase() === invalidValue.toLowerCase())) {
                            newHighlightMap[`${rowIdx}-${field}`] = true;
                          }
                        }
                      }
                    });
                  });
                }
              }
            });
          }
        }
      });
    }

    // 2. Add highlights from client-side mandatory field checks
    const hasDataRows = displayRows.some(row => Object.entries(row).some(([key, value]) => key !== 'tempKey' && value !== '' && value !== null));
    if (hasDataRows) {
        const clientSideErrors = highlightErrors(displayRows, tabType);
        for (const key in clientSideErrors) {
            newHighlightMap[key] = true;
        }
    }
    
    // 3. Add highlights from the Load Group validation state (managed by the hook)
    for (const key in validationErrors) {
        newHighlightMap[key] = true;
    }

    // 4. Set the final, combined highlight map
    setHighlightMap(newHighlightMap);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rows, 
    displayRows, 
    tabType, 
    highlightErrors, 
    isValidationInProgress, 
    alertSeverityFillTable, 
    alertMessageFillTable, 
    columns, 
    validationErrors
  ]);


  const onValidateClickFillTable = async () => {
     // First check for empty rows
     if (checkForEmptyRows()) {
       return; // Stop validation if there are empty rows
     }
     
     if (!validateMandatoryFields()) {
       return;
     }
      
      // Normalize Shipment Dispatch Date before sending to backend
      const normalizedRows = rows.map(normalizeShipmentDispatchDate);
      handleJsonValidation(
        selectedDC,
        tabType,
        setIsFileValidated,
        normalizedRows,
        setIsLoading,
        showSuccess,
        showError
      );
    
  };

  const onUploadClickFillTable = async () => {
     
      // Use real API
      // Normalize Shipment Dispatch Date before sending to backend
      const normalizedRows = rows.map(normalizeShipmentDispatchDate);
      
      // Count rows with actual data (exclude completely empty rows)
      const rowCount = normalizedRows.filter(row => 
        Object.entries(row).some(([key, value]) => key !== 'tempKey' && value !== '' && value !== null)
      ).length;
      
      // Store the exact data for potential proceed action
      setUploadNormalizedRows(normalizedRows);
      setUploadRowCount(rowCount);
      
      // Define a callback to handle the wave running scenario
      const handleWaveRunning = (waveData: any) => {
        setUploadResponse(waveData); // Store the API response
        setShowConfirmDialog(true);  // Show the confirmation dialog
      };

      handleJsonUpload(
        selectedDC,
        tabType,
        handleReset,
        normalizedRows,
        rowCount,
        setIsUploading,
        showSuccess,
        showError,
        handleWaveRunning, // Pass the callback for the wave running case
        false, // Default isProceed value
        userId,
        new Date().toISOString()
      );
    
  };

  useEffect(() => {
    setIsFileValidated(false);
    
    // Only clear validation in progress flag if rows data actually changed (not just reference)
    if (isValidationInProgress && rowsAtValidationRef.current.length > 0) {
      // Compare current rows with rows at validation time
      const rowsChanged = JSON.stringify(rows) !== JSON.stringify(rowsAtValidationRef.current);
      if (rowsChanged) {
        setIsValidationInProgress(false);
        rowsAtValidationRef.current = [];
      }
    }
  }, [rows, setIsFileValidated, isValidationInProgress]);

  // Clear highlights when component initializes or when there are no rows with data
  /* This useEffect is now redundant because of the combined effect above.
  useEffect(() => {
    const hasDataRows = rows.some(row => 
      Object.entries(row).some(([key, value]) => key !== 'tempKey' && value !== '' && value !== null)
    );
    if (!hasDataRows) {
      setHighlightMap({});
    }
  }, [rows, setHighlightMap]);
  */

  // Highlight error cells immediately after upload and table population (IN02 and XDOCK)
  /* This useEffect is now redundant because of the combined effect above.
  useEffect(() => {
    // Skip if validation is in progress to avoid overwriting validation highlights
    if (isValidationInProgress) {
      return;
    }
    
    if ((tabType === 'IN02' || tabType === 'XDOCK') && displayRows.length > 0) {
      // Get mandatory field highlights
      highlightErrors(displayRows, tabType);
    } else {
      setHighlightMap({});
    }
  }, [displayRows, tabType, highlightErrors, isValidationInProgress, setHighlightMap]);
  */

  /* This useEffect is now redundant because of the combined effect above.
  useEffect(() => {
    // Skip if validation is in progress to avoid overwriting validation highlights
    if (isValidationInProgress) {
      return;
    }
    
    if (alertSeverityFillTable === 'error' && alertMessageFillTable) {
      const map: Record<string, boolean> = {};
      // Split error message by "Error in line X"
      const errorLines = alertMessageFillTable.split(/Error in line/i).slice(1);
      errorLines.forEach(line => {
        // Match line number and message - updated regex to match "number X: message" or "number X - message" format
        const match = line.match(/\s*(?:number\s+)?(\d+)\s*[:-]\s*(.*)/);
        if (match) {
          const lineNum = (parseInt(match[1].trim()) - 1).toString();
          const message = match[2].trim();
          columns.forEach(({ key, name }) => {
            // Remove extra spaces and lowercase for comparison
            const normalizedLabel = name.replace(/\s+/g, '').toLowerCase();
            const normalizedKey = key.toLowerCase();
            const normalizedMessage = message.replace(/\s+/g, '').toLowerCase();

            // Highlight only columns with current errors
            const isFacilityError = normalizedMessage.includes('facility') && key === 'Facility';
            const isFieldError = normalizedMessage.includes(normalizedKey) || normalizedMessage.includes(normalizedLabel);
            const isLoadGroupField = key.startsWith('Load Group');

            if ((isFacilityError || isFieldError) && !isLoadGroupField) {
              const mapKey = `${lineNum}-${key}`;
              map[mapKey] = true;
            }
          });

          // Special cases for IN02 errors that highlight all matching cells
          if (tabType === 'IN02') {
            const specialCases = [
              {
                fields: ['Planned Door', 'Secondary Planned Door', 'Tertiary Planned Door'],
                messagePattern: "Door",
                regex: /Doors? '([^']+)' (?:are|is) not present in MA/,
              },
              {
                fields: ['Primary Staging Location', 'Secondary Staging Location', 'Tertiary Staging Location'],
                messagePattern: "Staging Location",
                regex: /Staging Locations? '([^']+)' (?:are|is) not present in MA/i,
              },
            ];

            specialCases.forEach(({ fields, messagePattern, regex }) => {
              if (message.includes(messagePattern)) {
                const match = message.match(regex);
                if (match) {
                  // Split on commas to handle multiple values like 'DOOR-15', 'DOOR-17'
                  const invalidValues = match[1].split(',').map(val => val.replace(/^['"]|['"]$/g, '').trim());
                  fields.forEach(field => {
                    const fieldKey = field;
                    if (fieldKey) {
                      // Highlight cells in the specific field that contain any of the invalid values
                      rows.forEach((row, rowIdx) => {
                        const cellValue = row[fieldKey]?.toString().trim().replace(/^['"]|['"]$/g, '');
                        if (cellValue && invalidValues.some(invalidValue => cellValue.toLowerCase() === invalidValue.toLowerCase())) {
                          const mapKey = `${rowIdx}-${field}`;
                          map[mapKey] = true;
                        }
                      });
                    }
                  });
                }
              }
            });
          }
        }
      });
      setHighlightMap(map);
    } else {
      // Clear highlights if no error or after error is fixed
      setHighlightMap({});
    }
  }, [alertSeverityFillTable, alertMessageFillTable, columns, rows, setHighlightMap, isValidationInProgress, tabType]);
  */

  useEffect(() => {
    setJumpToPageValue((currentPage + 1).toString());
  }, [currentPage, setJumpToPageValue]);

  // Add mouse drag selection state
  const [dragSelecting, setDragSelecting] = React.useState(false);
  const [dragStartCell, setDragStartCell] = React.useState<{ row: number; col: number } | null>(null);
  const [dragEndCell, setDragEndCell] = React.useState<{ row: number; col: number } | null>(null);
  const [selectedCellRect, setSelectedCellRect] = React.useState<{ rowMin: number; rowMax: number; colMin: number; colMax: number } | null>(null);

  // Helper to get selected cell rectangle
  const getSelectedCellRect = () => {
    if (!dragStartCell || !dragEndCell) return null;
    const rowMin = Math.min(dragStartCell.row, dragEndCell.row);
    const rowMax = Math.max(dragStartCell.row, dragEndCell.row);
    const colMin = Math.min(dragStartCell.col, dragEndCell.col);
    const colMax = Math.max(dragStartCell.col, dragEndCell.col);
    return { rowMin, rowMax, colMin, colMax };
  };

  return (
    <div>
      <div style={{ width: '100%', overflowX: 'auto', height: '60vh', overflowY: 'auto' }}>
        <table
          className="excel-table"
          ref={tableRef}
          onPaste={handlePaste}
          tabIndex={0}
          style={{ width: '100%' }}
          onMouseUp={() => {
            setDragSelecting(false);
            if (dragStartCell && dragEndCell) {
              setSelectedCellRect(getSelectedCellRect());
            }
          }}
          onMouseLeave={() => {
            setDragSelecting(false);
            if (dragStartCell && dragEndCell) {
              setSelectedCellRect(getSelectedCellRect());
            }
          }}
          onKeyDown={e => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
              e.preventDefault();
              // If cell selection exists, copy only selected cells
              const rect = selectedCellRect || getSelectedCellRect();
              if (rect) {
                const { rowMin, rowMax, colMin, colMax } = rect;
                const selectedRowsData = [];
                for (let r = rowMin; r <= rowMax; r++) {
                  const row = rows[r];
                  const selectedCols = columns.slice(colMin, colMax + 1);
                  selectedRowsData.push(selectedCols.map(col => row[col.key] ?? '').join('\t'));
                }
                const text = selectedRowsData.join('\n');
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(text);
                } else {
                  const textarea = document.createElement('textarea');
                  textarea.value = text;
                  document.body.appendChild(textarea);
                  textarea.select();
                  document.execCommand('copy');
                  document.body.removeChild(textarea);
                }
                return;
              }
              // If a single cell is being edited, copy just that cell
              if (editingCell) {
                const row = rows[editingCell.row];
                const text = row[editingCell.col] ?? '';
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(text);
                } else {
                  const textarea = document.createElement('textarea');
                  textarea.value = text;
                  document.body.appendChild(textarea);
                  textarea.select();
                  document.execCommand('copy');
                  document.body.removeChild(textarea);
                }
                return;
              }
              // Otherwise, copy selected rows by checkbox
              if (selectedRows.size > 0) {
                const visibleCols = columns.map(c => c.key);
                const rowsToCopy = Array.from(selectedRows).map(idx => rows[idx]);
                const text = rowsToCopy.map(row =>
                  visibleCols.map(colKey => row[colKey] ?? '').join('\t')
                ).join('\n');
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(text);
                } else {
                  const textarea = document.createElement('textarea');
                  textarea.value = text;
                  document.body.appendChild(textarea);
                  textarea.select();
                  document.execCommand('copy');
                  document.body.removeChild(textarea);
                }
              }
            } else if ((e.ctrlKey || e.metaKey) && e.key === 'x') {
              e.preventDefault();
              // Cut operation: copy then clear
              
              // First copy the content (reuse copy logic)
              const rect = selectedCellRect || getSelectedCellRect();
              if (rect) {
                const { rowMin, rowMax, colMin, colMax } = rect;
                const selectedRowsData = [];
                for (let r = rowMin; r <= rowMax; r++) {
                  const row = rows[r];
                  const selectedCols = columns.slice(colMin, colMax + 1);
                  selectedRowsData.push(selectedCols.map(col => row[col.key] ?? '').join('\t'));
                }
                const text = selectedRowsData.join('\n');
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(text);
                } else {
                  const textarea = document.createElement('textarea');
                  textarea.value = text;
                  document.body.appendChild(textarea);
                  textarea.select();
                  document.execCommand('copy');
                  document.body.removeChild(textarea);
                }
                
                // Then clear the selected cells using bulk clear
                const cellUpdates: { rowIndex: number; colKey: string }[] = [];
                for (let r = rowMin; r <= rowMax; r++) {
                  const selectedCols = columns.slice(colMin, colMax + 1);
                  selectedCols.forEach(col => {
                    cellUpdates.push({ rowIndex: r, colKey: col.key });
                  });
                }
                bulkClearCells(cellUpdates);
                return;
              }
              
              // If a single cell is being edited, cut just that cell
              if (editingCell) {
                const row = rows[editingCell.row];
                const text = row[editingCell.col] ?? '';
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(text);
                } else {
                  const textarea = document.createElement('textarea');
                  textarea.value = text;
                  document.body.appendChild(textarea);
                  textarea.select();
                  document.execCommand('copy');
                  document.body.removeChild(textarea);
                }
                // Clear the cell
                handleCellChange({ target: { value: '' } } as React.ChangeEvent<HTMLInputElement>, editingCell.row, editingCell.col);
                return;
              }
              
              // Otherwise, cut selected rows by checkbox
              if (selectedRows.size > 0) {
                const visibleCols = columns.map(c => c.key);
                const rowsToCopy = Array.from(selectedRows).map(idx => rows[idx]);
                const text = rowsToCopy.map(row =>
                  visibleCols.map(colKey => row[colKey] ?? '').join('\t')
                ).join('\n');
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(text);
                } else {
                  const textarea = document.createElement('textarea');
                  textarea.value = text;
                  document.body.appendChild(textarea);
                  textarea.select();
                  document.execCommand('copy');
                  document.body.removeChild(textarea);
                }
                
                // Clear the selected rows using bulk clear
                const cellUpdates: { rowIndex: number; colKey: string }[] = [];
                Array.from(selectedRows).forEach(rowIndex => {
                  visibleCols.forEach(colKey => {
                    cellUpdates.push({ rowIndex, colKey });
                  });
                });
                bulkClearCells(cellUpdates);
              }
            } else if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
              e.preventDefault();
              // Handle Ctrl+V paste operation
              navigator.clipboard.readText().then(text => {
                // Create a synthetic clipboard event with minimal required properties
                const syntheticEvent = {
                  preventDefault: () => {},
                  stopPropagation: () => {},
                  clipboardData: {
                    getData: () => text
                  }
                } as unknown as React.ClipboardEvent<HTMLTableElement>;
                handlePaste(syntheticEvent);
              }).catch(err => {
                console.error('Failed to read clipboard:', err);
              });
            } else if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
              // If currently editing a cell, blur it to exit edit mode
              if (editingCell && document.activeElement instanceof HTMLInputElement) {
                document.activeElement.blur();
                setEditingCell(null);
                
                // Refocus the table so subsequent Ctrl+C works
                if (tableRef.current) {
                  tableRef.current.focus();
                }
              }
              
              e.preventDefault();
              // Select all rows that have data
              const rowsWithData = new Set<number>();
              rows.forEach((row, index) => {
                const hasData = Object.entries(row).some(([key, value]) => 
                  key !== 'tempKey' && value !== '' && value !== null
                );
                if (hasData) {
                  rowsWithData.add(index);
                }
              });
              setSelectedRows(rowsWithData);
            }
          }}
        >
          <thead>
            <tr>
              <th style={{ width: '40px' }} />
              <th style={{ width: '60px' }}>
                <div className="header-content">
                  Sl #
                </div>
              </th>
              {columns.map((col, index) => (
                <th key={col.key}>
                  <div
                    className="header-content"
                    style={getMandatoryFieldKeys(tabType, selectedDC).includes(col.key) ? { fontWeight: 'bold' } : {}}
                  >
                    {col.name}
                    {getMandatoryFieldKeys(tabType, selectedDC).includes(col.key) ? 
                        <span style={{ color: 'red', marginLeft: '2px', fontWeight: 'bold' }}>*</span> : null}
                    <div className="resizer" onMouseDown={(e) => startResize(index, e)} />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows
              .slice(
                paginationModel.page * paginationModel.pageSize,
                (paginationModel.page + 1) * paginationModel.pageSize
              )
              .map((row, rowIndex) => {
                const actualRowIndex = paginationModel.page * paginationModel.pageSize + rowIndex;
                return (
                  <tr key={actualRowIndex}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedRows.has(actualRowIndex)}
                        onChange={() => toggleRowSelection(actualRowIndex)}
                      />
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {actualRowIndex + 1}
                    </td>
                    {columns.map((col, colIndex) => {
                      const key = `${actualRowIndex}-${col.key}`;
                      // Determine mandatory fields based on tab type and DC configuration
                      const mandatoryFields = getMandatoryFieldKeys(tabType, selectedDC);
                      
                      const isMandatory = mandatoryFields.includes(col.key);
                      const isEmpty = !row[col.key] || row[col.key].toString().trim() === '';
                      const hasRowData = Object.entries(row).some(([rowKey, value]) =>
                        rowKey !== 'tempKey' && value !== '' && value !== null
                      );
                      let shouldHighlightEmpty = false;
                      if (isMandatory) {
                        
                        // Check if this is Primary Staging Location or Planned Door and if Custom IN02 exemption applies
                        if ((col.key === 'Primary Staging Location' || col.key === 'Planned Door') && tabType === 'IN02') {
                          const customIN02DCs = Environment.getCustomIN02DCList();
                          const isCustomIN02DC = customIN02DCs.includes(selectedDC);
                          let isPrimaryStagingLocationExempt = false;
                          
                          // For Custom IN02 DCs: if Facility equals Stop Destination Facility, Primary Staging Location and Planned Door are not required
                          if (isCustomIN02DC) {
                            const facilityValue = row['Facility'];
                            const stopDestinationFacility = row['Stop Destination Facility'];
                            
                            if (facilityValue && stopDestinationFacility) {
                              const facility = facilityValue.toString().trim();
                              const stopDest = stopDestinationFacility.toString().trim();
                              isPrimaryStagingLocationExempt = facility === stopDest;
                            }
                          }
                          
                          // Don't highlight if exemption applies
                          if (!isPrimaryStagingLocationExempt) {
                            shouldHighlightEmpty = isMandatory && isEmpty && hasRowData;
                          }
                        } else {
                          shouldHighlightEmpty = isMandatory && isEmpty && hasRowData;
                        }
                      }
                      const hasValidationError = highlightMap[key];
                      let isFacilityInvalid = false;
                      if (col.key === 'Facility' && row[col.key] && row[col.key].toString().trim() !== '') {
                        const facilityValue = row[col.key].toString().trim();
                        
                        // Check length constraints (min 4, max 5 characters)
                        const isValidLength = facilityValue.length >= 4 && facilityValue.length <= 5;
                        
                        // Check if facility matches selected DC pattern
                        const allowedPatterns = [
                          selectedDC, // Exact DC match
                          `${selectedDC}C`, // DC + C
                          `${selectedDC}D`, // DC + D
                          `${selectedDC}Q`, // DC + Q
                          `${selectedDC}U`  // DC + U
                        ];
                        const isValidPattern = allowedPatterns.includes(facilityValue);
                        
                        isFacilityInvalid = !isValidLength || !isValidPattern;
                      }
                      const loadGroupFields = ['Load Group FRZ', 'Load Group GRO', 'Load Group PER', 'Load Group GML', 'Load Group PRO'];
                      const isLoadGroupField = tabType === 'IN02' && loadGroupFields.includes(col.key);
                      let shouldHighlightLoadGroup = false;
                      if (isLoadGroupField && hasRowData) {
                        const hasLoadGroup = loadGroupFields.some(field => {
                          const value = row[field];
                          return value && value !== null && value.toString().trim() !== '';
                        });
                        shouldHighlightLoadGroup = !hasLoadGroup && hasRowData;
                        
                        // Also check for real-time validation errors for this specific cell
                        const cellKey = `${actualRowIndex}-${col.key}`;
                        if (validationErrors[cellKey]) {
                          shouldHighlightLoadGroup = true;
                        }
                      }
                      
                      // Check for date validation errors
                      let isDateInvalid = false;
                      if (tabType === 'IN02' && col.key === 'Shipment Dispatch Date' && row[col.key] && row[col.key].toString().trim() !== '') {
                        const { isValid } = validateAndFormatDate(row[col.key].toString());
                        isDateInvalid = !isValid;
                      }
                      
                      // Check for Stop Destination Facility validation errors for Custom IN02
                      let isStopDestInvalid = false;
                      if (tabType === 'IN02' && col.key === 'Stop Destination Facility' && row[col.key] && row[col.key].toString().trim() !== '') {
                        const customIN02DCs = Environment.getCustomIN02DCList();
                        const isCustomIN02DC = customIN02DCs.includes(selectedDC);
                        
                        if (isCustomIN02DC) {
                          const stopDestValue = row[col.key].toString().trim();
                          if (stopDestValue.length >= 4) {
                            const first4Chars = stopDestValue.substring(0, 4);
                            const isCustomIN02StopDest = customIN02DCs.includes(first4Chars);
                            
                            if (isCustomIN02StopDest) {
                              // Check length constraints
                              if (stopDestValue.length < 4 || stopDestValue.length > 5) {
                                isStopDestInvalid = true;
                              }
                              // If 5 characters, check 5th character
                              else if (stopDestValue.length === 5) {
                                const fifthChar = stopDestValue.charAt(4).toUpperCase();
                                if (!['C', 'D', 'Q', 'U'].includes(fifthChar)) {
                                  isStopDestInvalid = true;
                                }
                              }
                            }
                          }
                        }
                      }
                      
                      // Check for Stop # validation errors for Custom IN02
                      let isStopInvalid = false;
                      if (tabType === 'IN02' && col.key === 'Stop #' && row[col.key] && row[col.key].toString().trim() !== '') {
                        const customIN02DCs = Environment.getCustomIN02DCList();
                        const isCustomIN02DC = customIN02DCs.includes(selectedDC);
                        
                        if (isCustomIN02DC) {
                          const stopValue = row[col.key].toString().trim();
                          // Check if Stop # contains an underscore
                          if (!stopValue.includes('_')) {
                            isStopInvalid = true;
                          }
                        }
                      }
                      // Highlight cell if in drag-selected rectangle
                      let dragCellHighlight = {};
                      const rect = selectedCellRect || getSelectedCellRect();
                      if (rect && actualRowIndex >= rect.rowMin && actualRowIndex <= rect.rowMax && colIndex >= rect.colMin && colIndex <= rect.colMax) {
                        dragCellHighlight = {
                          backgroundColor: '#cce5ff',
                          border: '2px solid #339af0'
                        };
                      }
                    // Single-cell active highlighting (only when no drag selection exists)
                      let activeCellHighlight = {};
                      if (!rect && editingCell?.row === actualRowIndex && editingCell.col === col.key) {
                        activeCellHighlight = {
                          backgroundColor: '#cce5ff',
                          border: '2px solid #339af0'
                        };
                      }

                      // Use only one type of highlighting - drag selection takes priority
                      const cellHighlight = rect ? dragCellHighlight : activeCellHighlight;
                      return (
                        <td
                          key={col.key}
                          onMouseDown={e => {
                            if (e.button === 0) {
                              setDragSelecting(true);
                              setDragStartCell({ row: actualRowIndex, col: colIndex });
                              setDragEndCell({ row: actualRowIndex, col: colIndex });
                              setSelectedCellRect(null); // Clear previous selection
                            }
                          }}
                          onMouseEnter={e => {
                            if (dragSelecting && dragStartCell) {
                              setDragEndCell({ row: actualRowIndex, col: colIndex });
                            }
                          }}
                          onClick={() => {
                            // Always clear drag selection when clicking
                            setSelectedCellRect(null);
                            setDragStartCell(null);
                            setDragEndCell(null);
                            setDragSelecting(false);
                            handleClick(actualRowIndex, col.key);
                          }}
                          className={
                            `${editingCell?.row === actualRowIndex && editingCell.col === col.key ? 'editing' : ''} ` +
                            `${hasValidationError ? 'highlight' : ''} ` +
                            `${shouldHighlightEmpty ? 'mandatory-empty' : ''} ` +
                            `${isFacilityInvalid ? 'facility-mismatch' : ''} ` +
                            `${shouldHighlightLoadGroup ? 'loadgroup-error' : ''} ` +
                            `${isDateInvalid ? 'date-invalid' : ''} ` +
                            `${isStopDestInvalid ? 'stopdest-invalid' : ''} ` +
                            `${isStopInvalid ? 'stop-invalid' : ''}`
                          }
                          style={{
                            border: (hasValidationError || shouldHighlightEmpty || isFacilityInvalid || shouldHighlightLoadGroup || isDateInvalid || isStopDestInvalid || isStopInvalid) ? '2px solid red' : undefined,
                            backgroundColor: (hasValidationError || shouldHighlightEmpty || isFacilityInvalid || shouldHighlightLoadGroup || isDateInvalid || isStopDestInvalid || isStopInvalid) ? '#ffebee' : undefined,
                            ...cellHighlight
                          }}
                        >
                          {editingCell?.row === actualRowIndex && editingCell.col === col.key ? (
                            <input
                              type="text"
                              value={col.key === 'Shipment ID' && row[col.key] ? 
                                (row[col.key] as string).replace(/'/g, '') : 
                                (tabType === 'IN02' && col.key === 'Facility' && row[col.key] ? 
                                  (row[col.key] as string).toUpperCase() : 
                                  col.key === 'Shipment Dispatch Date' && row[col.key] ?
                                    (row[col.key] as string).replace(/[-\\/]+/g, '/') :
                                    row[col.key as keyof Row] as any)}
                              onChange={(e) => {
                                
                                let value = e.target.value;
                                if (col.key === 'Shipment Dispatch Date') {
                                  value = value.replace(/[-\\/]+/g, '/');
                                }
                                
                                handleCellChange({
                                  ...e,
                                  target: {
                                    ...e.target,
                                    value,
                                  }
                                }, actualRowIndex, col.key);
                              }}
                              onInput={(e) => {
                                const target = e.target as HTMLInputElement;
                                const cursorPos = target.selectionStart;
                                
                                // Ensure immediate uppercase display for IN02 Facility
                                if (tabType === 'IN02' && col.key === 'Facility') {
                                  target.value = target.value.toUpperCase();
                                  target.setSelectionRange(cursorPos, cursorPos);
                                }
                                
                                // Limit Facility field to 5 characters max
                                if (col.key === 'Facility' && target.value.length > 5) {
                                  target.value = target.value.substring(0, 5);
                                  target.setSelectionRange(5, 5);
                                }
                              }}
                              onBlur={(e) => {
                                // Format date when user finishes editing Shipment Dispatch Date
                                if (tabType === 'IN02' && col.key === 'Shipment Dispatch Date' && e.target.value.trim() !== '') {
                                  const { isValid, formattedDate } = validateAndFormatDate(e.target.value);
                                  if (isValid && formattedDate !== e.target.value) {
                                    // Create a synthetic event to update the cell with formatted date
                                    const syntheticEvent = {
                                      target: { value: formattedDate }
                                    } as React.ChangeEvent<HTMLInputElement>;
                                    handleCellChange(syntheticEvent, actualRowIndex, col.key);
                                  }
                                }
                                setEditingCell(null);
                              }}
                              autoFocus
                              onKeyDown={e => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  (e.target as HTMLInputElement).blur();
                                  if (e.shiftKey) {
                                    // Shift+Enter: Move to previous row in same column
                                    if (actualRowIndex > 0) {
                                      setEditingCell({ row: actualRowIndex - 1, col: col.key });
                                    } else {
                                      // At the first row of current page - check if there's a previous page
                                      const currentPage = paginationModel.page;
                                      if (currentPage > 0) {
                                        // Go to previous page and focus on last row of same column
                                        setPaginationModel(prev => ({ ...prev, page: currentPage - 1 }));
                                        const lastRowOfPrevPage = currentPage * paginationModel.pageSize - 1;
                                        setEditingCell({ row: lastRowOfPrevPage, col: col.key });
                                      } else {
                                        // At the first row of first page - stay in current cell
                                        setEditingCell({ row: actualRowIndex, col: col.key });
                                      }
                                    }
                                  } else {
                                    // Enter: Move to next row in same column
                                    if (actualRowIndex < displayRows.length - 1) {
                                      setEditingCell({ row: actualRowIndex + 1, col: col.key });
                                    } else {
                                      // At the last row of current page - check if there's a next page
                                      const currentPage = paginationModel.page;
                                      const totalPages = Math.ceil(displayRows.length / paginationModel.pageSize);
                                      if (currentPage + 1 < totalPages) {
                                        // Go to next page and focus on first row of same column
                                        setPaginationModel(prev => ({ ...prev, page: currentPage + 1 }));
                                        const firstRowOfNextPage = (currentPage + 1) * paginationModel.pageSize;
                                        setEditingCell({ row: firstRowOfNextPage, col: col.key });
                                      } else {
                                        // At the last row of last page - stay in current cell
                                        setEditingCell({ row: actualRowIndex, col: col.key });
                                      }
                                    }
                                  }
                                } else if (e.key === 'ArrowUp') {
                                  e.preventDefault();
                                  if (actualRowIndex > 0) {
                                    (e.target as HTMLInputElement).blur();
                                    setEditingCell({ row: actualRowIndex - 1, col: col.key });
                                  } else {
                                    // At the first row of current page - check if there's a previous page
                                    const currentPage = paginationModel.page;
                                    if (currentPage > 0) {
                                      // Go to previous page and focus on last row of same column
                                      (e.target as HTMLInputElement).blur();
                                      setPaginationModel(prev => ({ ...prev, page: currentPage - 1 }));
                                      const lastRowOfPrevPage = currentPage * paginationModel.pageSize - 1;
                                      setEditingCell({ row: lastRowOfPrevPage, col: col.key });
                                    }
                                  }
                                } else if (e.key === 'ArrowDown') {
                                  e.preventDefault();
                                  if (actualRowIndex < displayRows.length - 1) {
                                    (e.target as HTMLInputElement).blur();
                                    setEditingCell({ row: actualRowIndex + 1, col: col.key });
                                  } else {
                                    // At the last row of current page - check if there's a next page
                                    const currentPage = paginationModel.page;
                                    const totalPages = Math.ceil(displayRows.length / paginationModel.pageSize);
                                    if (currentPage + 1 < totalPages) {
                                      // Go to next page and focus on first row of same column
                                      (e.target as HTMLInputElement).blur();
                                      setPaginationModel(prev => ({ ...prev, page: currentPage + 1 }));
                                      const firstRowOfNextPage = (currentPage + 1) * paginationModel.pageSize;
                                      setEditingCell({ row: firstRowOfNextPage, col: col.key });
                                    }
                                  }
                                } else if (e.key === 'ArrowLeft') {
                                  e.preventDefault();
                                  const colIndex = columns.findIndex(c => c.key === col.key);
                                  if (colIndex > 0) {
                                    (e.target as HTMLInputElement).blur();
                                    setEditingCell({ row: actualRowIndex, col: columns[colIndex - 1].key });
                                  } else if (actualRowIndex > 0) {
                                    // At first column, move to last column of previous row
                                    (e.target as HTMLInputElement).blur();
                                    setEditingCell({ row: actualRowIndex - 1, col: columns[columns.length - 1].key });
                                  } else {
                                    // At first cell of current page - check if there's a previous page
                                    const currentPage = paginationModel.page;
                                    if (currentPage > 0) {
                                      // Go to previous page and focus on last cell
                                      (e.target as HTMLInputElement).blur();
                                      setPaginationModel(prev => ({ ...prev, page: currentPage - 1 }));
                                      const lastRowOfPrevPage = currentPage * paginationModel.pageSize - 1;
                                      setEditingCell({ row: lastRowOfPrevPage, col: columns[columns.length - 1].key });
                                    }
                                  }
                                } else if (e.key === 'ArrowRight') {
                                  e.preventDefault();
                                  const colIndex = columns.findIndex(c => c.key === col.key);
                                  if (colIndex < columns.length - 1) {
                                    (e.target as HTMLInputElement).blur();
                                    setEditingCell({ row: actualRowIndex, col: columns[colIndex + 1].key });
                                  } else if (actualRowIndex < displayRows.length - 1) {
                                    // At last column, move to first column of next row
                                    (e.target as HTMLInputElement).blur();
                                    setEditingCell({ row: actualRowIndex + 1, col: columns[0].key });
                                  } else {
                                    // At last cell of current page - check if there's a next page
                                    const currentPage = paginationModel.page;
                                    const totalPages = Math.ceil(displayRows.length / paginationModel.pageSize);
                                    if (currentPage + 1 < totalPages) {
                                      // Go to next page and focus on first cell
                                      (e.target as HTMLInputElement).blur();
                                      setPaginationModel(prev => ({ ...prev, page: currentPage + 1 }));
                                      const firstRowOfNextPage = (currentPage + 1) * paginationModel.pageSize;
                                      setEditingCell({ row: firstRowOfNextPage, col: columns[0].key });
                                    }
                                  }
                                } else if (e.key === 'Tab') {
                                  e.preventDefault();
                                  (e.target as HTMLInputElement).blur();
                                  const colIndex = columns.findIndex(c => c.key === col.key);                                 
                                  
                                  if (e.shiftKey) {
                                    // Shift+Tab: Move to previous cell
                                    if (colIndex > 0) {
                                      // Move to previous column in same row
                                      setEditingCell({ row: actualRowIndex, col: columns[colIndex - 1].key });
                                    } else if (actualRowIndex > 0) {
                                      // Move to last column of previous row
                                      setEditingCell({ row: actualRowIndex - 1, col: columns[columns.length - 1].key });
                                    } else {
                                      // At the first cell of current page - check if there's a previous page
                                      const currentPage = paginationModel.page;
                                      if (currentPage > 0) {
                                        // Go to previous page and focus on last cell
                                        setPaginationModel(prev => ({ ...prev, page: currentPage - 1 }));
                                        const lastRowOfPrevPage = currentPage * paginationModel.pageSize - 1;
                                        setEditingCell({ row: lastRowOfPrevPage, col: columns[columns.length - 1].key });
                                      } else {
                                        // At the first cell of first page - stay in current cell
                                        setEditingCell({ row: actualRowIndex, col: col.key });
                                      }
                                    }
                                  } else {
                                    // Tab: Move to next cell
                                    if (colIndex < columns.length - 1) {
                                      // Move to next column in same row
                                      setEditingCell({ row: actualRowIndex, col: columns[colIndex + 1].key });
                                    } else if (actualRowIndex < displayRows.length - 1) {
                                      // Move to first column of next row
                                      setEditingCell({ row: actualRowIndex + 1, col: columns[0].key });
                                    } else {
                                      // At the last cell of current page - check if there's a next page
                                      const currentPage = paginationModel.page;
                                      const totalPages = Math.ceil(displayRows.length / paginationModel.pageSize);
                                      if (currentPage + 1 < totalPages) {
                                        // Go to next page and focus on first cell
                                        setPaginationModel(prev => ({ ...prev, page: currentPage + 1 }));
                                        const firstRowOfNextPage = (currentPage + 1) * paginationModel.pageSize;
                                        setEditingCell({ row: firstRowOfNextPage, col: columns[0].key });
                                      } else {
                                        // At the last cell of last page - stay in current cell
                                        setEditingCell({ row: actualRowIndex, col: col.key });
                                      }
                                    }
                                  }
                                }
                              }}
                              style={{
                                textTransform: tabType === 'IN02' && columns[colIndex].key === 'Facility' ? 'uppercase' : 'none'
                              }}
                              maxLength={col.key === 'Facility' ? 5 : undefined}
                              onPaste={e => {
                                e.preventDefault();
                                setEditingCell(null);
                                setTimeout(() => {
                                  if (tableRef.current) {
                                    tableRef.current.focus();
                                  }
                                }, 0);
                              }}
                            />
                          ) : (
                            (col.key === 'Shipment Dispatch Date' && !row[col.key]) ? (
                              <span style={{ color: '#aaa', fontSize: '12px' }}>mm/dd/yyyy hh:mm</span>
                            ) : (
                              col.key === labelDateKey && !row[col.key] ? (
                                <span style={{ color: '#aaa', fontSize: '12px' }}>mm/dd/yyyy hh:mm</span>
                              ) : (
                                <span style={{
                                  textTransform: tabType === 'IN02' && col.key === 'Facility' ? 'uppercase' : 'none'
                                }}>
                                  {col.key === 'Shipment ID' && row[col.key] ? 
                                    (row[col.key] as string).replace(/'/g, '') : 
                                    (tabType === 'IN02' && col.key === 'Shipment Dispatch Date' && row[col.key] ?
                                      (() => {
                                        const { isValid, formattedDate } = validateAndFormatDate(row[col.key].toString());
                                        return isValid ? formattedDate : row[col.key];
                                      })() :
                                      row[col.key as keyof Row])}
                                </span>
                              )
                            )
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      <div>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', zIndex: 1 }}>
            <Button width={120} onClick={addRow} disabled={isUploading || isLoading}>
              Add Row
            </Button>
            <Button width={120} onClick={handleDeleteAndReindexErrors} disabled={selectedRows.size === 0 || isUploading || isLoading}>
              Delete Row
            </Button>
            <Button width={120} onClick={handleCopySelectedRows} disabled={selectedRows.size === 0 || isLoading || isUploading}>
              Copy Row
            </Button>
            {/* Rows per Page */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Items:</span>
              <Select
                value={paginationModel.pageSize}
                onChange={(event) => handlePageSizeChange(Number(event.target.value))}
                size="small"
                style={{ width: '80px' }}
              >
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={75}>75</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </div>
            {/* Pagination Range */}
            <div>
              {displayRows.length === 0
                ? '0–0 of 0'
                : `${paginationModel.page * paginationModel.pageSize + 1}–${Math.min(
                  (paginationModel.page + 1) * paginationModel.pageSize,
                  displayRows.length
                )} of ${displayRows.length}`}
            </div>
          </div>
          {/* Right Section: Rows per Page and Jump to */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', zIndex: 1 }}>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {/* Previous */}
              <div
                style={{
                  padding: '4px 8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                  backgroundColor: currentPage === 0 ? '#f5f5f5' : 'white',
                }}
                onClick={() => {
                  if (currentPage > 0) {
                    setPaginationModel((prev) => ({ ...prev, page: currentPage - 1 }));
                  }
                }}
              >
                &lt;
              </div>
              {/* Page Numbers + Ellipsis */}
              {createPageRange().map((page, index) => (
                typeof page === 'number' ? (
                  <div
                    key={index}
                    style={{
                      padding: '4px 8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      backgroundColor: page === currentPage ? '#007bff' : 'white',
                      color: page === currentPage ? 'white' : 'black',
                    }}
                    onClick={() => setPaginationModel((prev) => ({ ...prev, page }))}
                  >
                    {page + 1}
                  </div>
                ) : (
                  <div key={index} style={{ padding: '4px 8px', color: '#999' }}>
                    ...
                  </div>
                )
              ))}

              {/* Next */}
              <div
                style={{
                  padding: '4px 8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: (currentPage + 1) * paginationModel.pageSize >= rows.length ? 'not-allowed' : 'pointer',
                  backgroundColor: (currentPage + 1) * paginationModel.pageSize >= rows.length ? '#f5f5f5' : 'white',
                }}
                onClick={() => {
                  if ((currentPage + 1) * paginationModel.pageSize < rows.length) {
                    setPaginationModel((prev) => ({ ...prev, page: currentPage + 1 }));
                  }
                }}
              >
                &gt;
              </div>
            </div>
            {/* Jump to Page */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Jump to:</span>
              <TextField
                size="small"
                type="number"
                inputProps={{ min: 1, max: Math.ceil(displayRows.length / paginationModel.pageSize) }}
                value={jumpToPageValue}
                onChange={e => setJumpToPageValue(e.target.value)}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleJumpToPage(e)}
                placeholder="Page #"
                style={{ width: '90px' }}
              />
            </div>
            {/* Error Message */}
            {jumpToPageError && (
              <div style={{
                backgroundColor: '#d32f2f',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '4px',
                margin: '8px 0',
                fontSize: '14px'
              }}>
                {jumpToPageError}
              </div>
            )}

          </div>
        </Box>
      </div>
      <div className="buttonContainerFillTable">
        <div className="closeButton">
          <Button
            width={120}
            onClick={handleCloseWithToastCleanup}
          >
            Close
          </Button>
        </div>
        <div className="clearButton">
          <Button
            width={100}
            disabled={
              isLoading || isUploading ||
              (filesFillTable.length === 0 && !rows.some(row =>
                Object.entries(row).some(([key, value]) => key !== 'tempKey' && value !== '' && value !== null)
              ))
            }
            onClick={() => {
              handleReset();
              setFilesFillTable([]); // Clear files for current tab
              setHighlightMap({}); // Clear cell highlights
              setValidationErrors({}); // Clear validation errors
              setAlertMessageFillTable(null); // Clear alert messages
              setAlertSeverityFillTable('success'); // Reset alert severity
            }}
          >
            Clear
          </Button>
        </div>
        <div className="validateButton">
          <Button width={120}
            disabled={!selectedDC.startsWith('W') || (!rows.some(row =>
              Object.entries(row).some(([key, value]) => key !== 'tempKey' && value !== '' && value !== null))) || isLoading || isUploading}
            onClick={onValidateClickFillTable}
          >
            Validate
          </Button>
          {isLoading && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
              <CircularProgress />
            </div>
          )}
        </div>
        <div className="uploadButton">
          <Button width={120}
            disabled={!isFileValidated || (filesFillTable.length === 0 && !rows.some(row =>
              Object.entries(row).some(([key, value]) => key !== 'tempKey' && value !== '' && value !== null))) || isLoading || isUploading}
            onClick={onUploadClickFillTable}
          >
            Upload to MA
          </Button>
          {isUploading && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
              <CircularProgress />
            </div>
          )}
        </div>
      </div>



      {/* Error Modal for viewing all errors */}
      <ErrorModal
        open={errorModalOpen}
        onClose={handleCloseErrorModal}
        errors={allErrors}
        title={errorModalTitle}
      />

      {/* Confirmation Dialog for Wave Running */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancel}
        aria-labelledby="wave-confirm-dialog-title"
        aria-describedby="wave-confirm-dialog-description"
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            zIndex: 9999,
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
        style={{ zIndex: 9999 }}
      >
        <DialogTitle 
          id="wave-confirm-dialog-title"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            padding: '20px 24px',
            fontSize: '20px',
            fontWeight: '600',
            letterSpacing: '0.5px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}
        >
          <span style={{
            fontSize: '24px',
            animation: 'pulse 2s infinite'
          }}>⚠️</span>
          Wave is Currently Running
        </DialogTitle>
        <DialogContent style={{ padding: '24px' }}>
          <DialogContentText 
            id="wave-confirm-dialog-description" 
            style={{ 
              marginBottom: '20px',
              fontSize: '15px',
              color: '#424242',
              lineHeight: '1.6',
              backgroundColor: '#fff3e0',
              padding: '16px',
              borderRadius: '8px',
              borderLeft: '4px solid #ff9800',
              fontWeight: '500'
            }}
          >
            <span style={{ fontSize: '18px', marginRight: '8px' }}>⚡</span>
            Below waves are currently running. Do you want to proceed with the upload?
          </DialogContentText>
          
          {/* Display upload data in a table */}
          {uploadResponse && uploadResponse.waveStatusApiResponseList && uploadResponse.waveStatusApiResponseList.length > 0 && (
            <div style={{ 
              overflowX: 'auto', 
              overflowY: 'auto',
              maxHeight: '400px',
              marginTop: '16px',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                fontSize: '13px'
              }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white'
                  }}>
                    <th style={{ 
                      padding: '10px 8px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      fontSize: '12px',
                      letterSpacing: '0.5px',
                      borderRight: '1px solid rgba(255,255,255,0.2)'
                    }}>
                      Order Planning Run ID
                    </th>
                    <th style={{ 
                      padding: '10px 8px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      fontSize: '12px',
                      letterSpacing: '0.5px',
                      borderRight: '1px solid rgba(255,255,255,0.2)'
                    }}>
                      Description
                    </th>
                    <th style={{ 
                      padding: '10px 8px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      fontSize: '12px',
                      letterSpacing: '0.5px',
                      borderRight: '1px solid rgba(255,255,255,0.2)'
                    }}>
                      Created By
                    </th>
                    <th style={{ 
                      padding: '10px 8px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      fontSize: '12px',
                      letterSpacing: '0.5px'
                    }}>
                      Created Timestamp
                    </th>
                    <th style={{ 
                      padding: '10px 8px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      fontSize: '12px',
                      letterSpacing: '0.5px',
                      borderRight: '1px solid rgba(255,255,255,0.2)'
                    }}>
                      Time Elapsed
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {uploadResponse.waveStatusApiResponseList.map((item: any, index: number) => (
                    <tr 
                      key={index} 
                      style={{ 
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                        transition: 'background-color 0.2s',
                        cursor: 'default'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e3f2fd'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f8f9fa'}
                    >
                      <td style={{ 
                        padding: '8px', 
                        borderBottom: '1px solid #e0e0e0',
                        color: '#424242'
                      }}>
                        {item.OrderPlanningRunId}
                      </td>
                      <td style={{ 
                        padding: '8px', 
                        borderBottom: '1px solid #e0e0e0',
                        color: '#616161',
                        maxWidth: '250px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }} title={item.Description}>
                        {item.Description}
                      </td>
                      <td style={{ 
                        padding: '8px', 
                        borderBottom: '1px solid #e0e0e0',
                        color: '#424242'
                      }}>
                        <span style={{
                          backgroundColor: '#e8eaf6',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '500',
                          color: '#3f51b5'
                        }}>
                          {item.CreatedBy}
                        </span>
                      </td>
                      <td style={{ 
                        padding: '8px', 
                        borderBottom: '1px solid #e0e0e0',
                        color: '#757575',
                        fontSize: '12px'
                      }}>
                        {new Date(item.CreatedTimestamp).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                        {/* Time Elapsed column */}
                        <td style={{
                          padding: '8px',
                          borderBottom: '1px solid #e0e0e0',
                          color: '#424242'
                        }}>
                          {item.timeElapsed}
                        </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
        <DialogActions style={{ 
          padding: '16px 24px', 
          backgroundColor: '#f5f5f5',
          gap: '12px',
          justifyContent: 'flex-end'
        }}>
          <Button 
            onClick={handleCancel} 
            variant="secondary"
            width={120}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleProceed} 
            variant="primary"
            width={120}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
}